/**
 * 增强版可视区域动画工具类
 * 支持Vue项目中的使用
 * 扩展了丰富的 scale 动画效果
 * 支持淡入+缩放的组合动画
 */
class EnhancedIntersectionUtils {
  constructor() {
    this.observers = new Map();
    this.init();
  }

  init() {
    this.autoInit();
    this.observeDOMChanges();
  }

  autoInit() {
    const selectors = [
      '[data-animation]',
      '[data-intersection]',
      '.animate-on-scroll',
      '.fade-in',
    ];

    selectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => this.processElement(el));
    });
  }

  processElement(element) {
    const config = this.parseElementConfig(element);
    this.applyAnimation(element, config);
  }

  parseElementConfig(element) {
    const config = {
      animation: 'fade-up',
      delay: 0,
      duration: 0.6,
      once: true,
      threshold: 0.1,
      rootMargin: '0px',
      // scale 相关配置
      scaleFrom: 0.8,
      scaleTo: 1.0,
      scaleEasing: 'ease-out',
      // 淡入相关配置
      fadeFrom: 0,
      fadeTo: 1,
      fadeEasing: 'ease-out',
    };

    // 从 data 属性读取配置
    if (element.dataset.animation) {
      config.animation = element.dataset.animation;
    }
    if (element.dataset.delay) {
      config.delay = parseFloat(element.dataset.delay);
    }
    if (element.dataset.duration) {
      config.duration = parseFloat(element.dataset.duration);
    }
    if (element.dataset.once !== undefined) {
      if (typeof element.dataset.once === 'string') {
        config.once = element.dataset.once === 'true';
      } else {
        config.once = Boolean(element.dataset.once);
      }
    }
    if (element.dataset.threshold) {
      config.threshold = parseFloat(element.dataset.threshold);
    }
    if (element.dataset.rootMargin) {
      config.rootMargin = element.dataset.rootMargin;
    }

    // scale 相关配置
    if (element.dataset.scaleFrom) {
      config.scaleFrom = parseFloat(element.dataset.scaleFrom);
    }
    if (element.dataset.scaleTo) {
      config.scaleTo = parseFloat(element.dataset.scaleTo);
    }
    if (element.dataset.scaleEasing) {
      config.scaleEasing = element.dataset.scaleEasing;
    }

    // 淡入相关配置
    if (element.dataset.fadeFrom) {
      config.fadeFrom = parseFloat(element.dataset.fadeFrom);
    }
    if (element.dataset.fadeTo) {
      config.fadeTo = parseFloat(element.dataset.fadeTo);
    }
    if (element.dataset.fadeEasing) {
      config.fadeEasing = element.dataset.fadeEasing;
    }

    // 从类名读取配置
    const classes = element.className.split(' ');
    classes.forEach((cls) => {
      if (cls.startsWith('delay-')) {
        config.delay = parseFloat(cls.replace('delay-', ''));
      }
      if (cls.startsWith('duration-')) {
        config.duration = parseFloat(cls.replace('duration-', ''));
      }
      if (cls === 'repeat' || cls === 'repeat-animation') {
        config.once = false;
      }
      if (cls === 'once' || cls === 'once-animation') {
        config.once = true;
      }
      if (cls.startsWith('scale-from-')) {
        config.scaleFrom = parseFloat(cls.replace('scale-from-', ''));
      }
      if (cls.startsWith('scale-to-')) {
        config.scaleTo = parseFloat(cls.replace('scale-to-', ''));
      }
      if (cls.startsWith('fade-from-')) {
        config.fadeFrom = parseFloat(cls.replace('fade-from-', ''));
      }
      if (cls.startsWith('fade-to-')) {
        config.fadeTo = parseFloat(cls.replace('fade-to-', ''));
      }
    });

    return config;
  }

  applyAnimation(element, config) {
    // 设置初始状态
    this.resetElementState(element, config.animation, config);

    // 根据动画类型设置不同的过渡效果
    if (
      config.animation.includes('fade-scale') ||
      config.animation.includes('scale-fade')
    ) {
      // 淡入+缩放的组合动画
      element.style.transition = `opacity ${config.duration}s ${config.fadeEasing}, transform ${config.duration}s ${config.scaleEasing}`;
    } else if (config.animation.includes('scale')) {
      element.style.transition = `opacity ${config.duration}s ease-out, transform ${config.duration}s ${config.scaleEasing}`;
    } else {
      element.style.transition = `all ${config.duration}s ease-out`;
    }

    if (config.delay > 0) {
      element.style.transitionDelay = `${config.delay}s`;
    }

    // 应用动画类型
    this.applyAnimationType(element, config.animation, config);

    // 创建观察器
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.showElement(element, config.animation, config);
            if (config.once) {
              observer.unobserve(element);
            }
          } else if (!config.once) {
            this.hideElement(element, config.animation, config);
          }
        });
      },
      {
        threshold: config.threshold,
        rootMargin: config.rootMargin,
      }
    );

    observer.observe(element);
    this.observers.set(element, observer);
  }

  resetElementState(element, animationType, config) {
    // 根据动画类型设置初始透明度
    if (animationType.includes('fade')) {
      element.style.opacity = config.fadeFrom;
    } else {
      element.style.opacity = '0';
    }

    // 清除之前的动画
    element.style.animation = '';

    // 重置 transform
    this.applyAnimationType(element, animationType, config);
  }

  applyAnimationType(element, animationType, config) {
    const animations = {
      // 原有的淡入动画
      'fade-up': { transform: 'translateY(30px)' },
      'fade-down': { transform: 'translateY(-30px)' },
      'fade-left': { transform: 'translateX(30px)' },
      'fade-right': { transform: 'translateX(-30px)' },
      fade: { transform: 'none' },

      // 新增：淡入+缩放的组合动画
      'fade-scale': { transform: `scale(${config.scaleFrom})` },
      'fade-scale-up': {
        transform: `scale(${config.scaleFrom}) translateY(30px)`,
      },
      'fade-scale-down': {
        transform: `scale(${config.scaleFrom}) translateY(-30px)`,
      },
      'fade-scale-left': {
        transform: `scale(${config.scaleFrom}) translateX(30px)`,
      },
      'fade-scale-right': {
        transform: `scale(${config.scaleFrom}) translateX(-30px)`,
      },

      // 缩放+淡入的组合动画
      'scale-fade': { transform: `scale(${config.scaleFrom})` },
      'scale-fade-up': {
        transform: `scale(${config.scaleFrom}) translateY(30px)`,
      },
      'scale-fade-down': {
        transform: `scale(${config.scaleFrom}) translateY(-30px)`,
      },
      'scale-fade-left': {
        transform: `scale(${config.scaleFrom}) translateX(30px)`,
      },
      'scale-fade-right': {
        transform: `scale(${config.scaleFrom}) translateX(-30px)`,
      },

      // 基础 scale 动画
      scale: { transform: `scale(${config.scaleFrom})` },
      'scale-up': { transform: `scale(${config.scaleFrom})` },
      'scale-down': { transform: `scale(${config.scaleTo + 0.2})` },

      // 增强的 scale 动画
      'scale-bounce': { transform: `scale(${config.scaleFrom})` },
      'scale-elastic': { transform: `scale(${config.scaleFrom})` },
      'scale-spring': { transform: `scale(${config.scaleFrom})` },
      'scale-pulse': { transform: `scale(${config.scaleFrom})` },
      'scale-breath': { transform: `scale(${config.scaleFrom})` },

      // 原有的组合动画
      'scale-slide-up': {
        transform: `scale(${config.scaleFrom}) translateY(50px)`,
      },
      'scale-slide-down': {
        transform: `scale(${config.scaleFrom}) translateY(-50px)`,
      },
      'scale-rotate': {
        transform: `scale(${config.scaleFrom}) rotate(-180deg)`,
      },
      'scale-flip': { transform: `scale(${config.scaleFrom}) rotateY(90deg)` },

      // 3D scale 效果
      'scale-3d': {
        transform: `scale3d(${config.scaleFrom}, ${config.scaleFrom}, ${config.scaleFrom})`,
      },
      'scale-perspective': {
        transform: `perspective(1000px) scale(${config.scaleFrom})`,
        transformStyle: 'preserve-3d',
      },

      // 原有的其他动画
      'slide-up': { transform: 'translateY(100%)' },
      'slide-down': { transform: 'translateY(-100%)' },
      bounce: { transform: 'scale(0.3)' },
      rotate: { transform: 'rotate(-180deg) scale(0.5)' },
      'zoom-in': { transform: 'scale(0.5)' },
      'zoom-out': { transform: 'scale(1.5)' },
      flip: { transform: 'perspective(400px) rotateY(90deg)' },
      'slide-left': { transform: 'translateX(100%)' },
      'slide-right': { transform: 'translateX(-100%)' },
    };

    const animation = animations[animationType] || animations['fade-up'];
    Object.assign(element.style, animation);
  }

  showElement(element, animationType, config) {
    // 设置最终透明度
    if (animationType.includes('fade')) {
      element.style.opacity = config.fadeTo;
    } else {
      element.style.opacity = '1';
    }

    const transforms = {
      // 原有的淡入动画
      'fade-up': 'translateY(0)',
      'fade-down': 'translateY(0)',
      'fade-left': 'translateX(0)',
      'fade-right': 'translateX(0)',
      fade: 'none',

      // 新增：淡入+缩放的组合动画
      'fade-scale': `scale(${config.scaleTo})`,
      'fade-scale-up': `scale(${config.scaleTo}) translateY(0)`,
      'fade-scale-down': `scale(${config.scaleTo}) translateY(0)`,
      'fade-scale-left': `scale(${config.scaleTo}) translateX(0)`,
      'fade-scale-right': `scale(${config.scaleTo}) translateX(0)`,

      // 缩放+淡入的组合动画
      'scale-fade': `scale(${config.scaleTo})`,
      'scale-fade-up': `scale(${config.scaleTo}) translateY(0)`,
      'scale-fade-down': `scale(${config.scaleTo}) translateY(0)`,
      'scale-fade-left': `scale(${config.scaleTo}) translateX(0)`,
      'scale-fade-right': `scale(${config.scaleTo}) translateX(0)`,

      // 基础 scale 动画
      scale: `scale(${config.scaleTo})`,
      'scale-up': `scale(${config.scaleTo})`,
      'scale-down': `scale(${config.scaleTo})`,

      // 增强的 scale 动画
      'scale-bounce': `scale(${config.scaleTo})`,
      'scale-elastic': `scale(${config.scaleTo})`,
      'scale-spring': `scale(${config.scaleTo})`,
      'scale-pulse': `scale(${config.scaleTo})`,
      'scale-breath': `scale(${config.scaleTo})`,

      // 原有的组合动画
      'scale-slide-up': `scale(${config.scaleTo}) translateY(0)`,
      'scale-slide-down': `scale(${config.scaleTo}) translateY(0)`,
      'scale-rotate': `scale(${config.scaleTo}) rotate(0deg)`,
      'scale-flip': `scale(${config.scaleTo}) rotateY(0deg)`,

      // 3D scale 效果
      'scale-3d': `scale3d(${config.scaleTo}, ${config.scaleTo}, ${config.scaleTo})`,
      'scale-perspective': `perspective(1000px) scale(${config.scaleTo})`,

      // 原有的其他动画
      'slide-up': 'translateY(0)',
      'slide-down': 'translateY(0)',
      bounce: 'scale(1)',
      rotate: 'rotate(0deg) scale(1)',
      'zoom-in': 'scale(1)',
      'zoom-out': 'scale(1)',
      flip: 'perspective(400px) rotateY(0deg)',
      'slide-left': 'translateX(0)',
      'slide-right': 'translateX(0)',
    };

    element.style.transform = transforms[animationType] || 'translateY(0)';

    // 特殊动画处理
    if (animationType === 'bounce') {
      element.style.animation = 'bounce 0.6s ease-out';
    }

    if (animationType === 'scale-bounce') {
      element.style.animation = 'scaleBounce 0.8s ease-out';
    }

    if (animationType === 'scale-elastic') {
      element.style.animation = 'scaleElastic 1.2s ease-out';
    }

    if (animationType === 'scale-spring') {
      element.style.animation = 'scaleSpring 1.0s ease-out';
    }

    if (animationType === 'scale-pulse') {
      element.style.animation = 'scalePulse 1.5s ease-in-out infinite';
    }

    if (animationType === 'scale-breath') {
      element.style.animation = 'scaleBreath 3s ease-in-out infinite';
    }
  }

  hideElement(element, animationType, config) {
    this.resetElementState(element, animationType, config);
  }

  observeDOMChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (
              node.matches &&
              (node.matches('[data-animation]') ||
                node.matches('[data-intersection]'))
            ) {
              this.processElement(node);
            }

            const children = node.querySelectorAll
              ? node.querySelectorAll('[data-animation], [data-intersection]')
              : [];
            children.forEach((child) => this.processElement(child));
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  add(element, config = {}) {
    this.applyAnimation(element, config);
  }

  remove(element) {
    if (this.observers.has(element)) {
      this.observers.get(element).disconnect();
      this.observers.delete(element);
    }
  }

  destroy() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
  }
}

// 创建单例实例
const enhancedUtils = new EnhancedIntersectionUtils();

// 导出实例和类
export default enhancedUtils;
export { EnhancedIntersectionUtils };
